import React from 'react';

const Gdpr = () => {
    return (
        <div className="p-5">
            <h1 className="text-center my-5 fontSize48">General Data Protection Regulation</h1>
            
            <h2 className="my-4">Introduction</h2>
            <p>
                The EU General Data Protection Regulation (GDPR) came into effect on May 25, 2018.
                Although it is an EU regulation, its scope extends beyond the EU, affecting any entity that
                processes personal data of EU citizens, regardless of location. This document provides
                an overview of individual rights under the GDPR and how these rights impact WiserBee
                customers.
            </p>
            <p>
                If you have been granted access to WiserBee services by your institution
                (educational or otherwise), employer, or organisation ("Organisation"), you should
                contact them directly to understand their policies and practices regarding your
                personal data.
            </p>
            <p>
                This document is intended for informational purposes only and should not be
                considered legal advice. For specific legal guidance, consult a qualified legal
                professional familiar with your compliance requirements.
            </p>

            <h2 className="my-4">WiserBee's Approach to GDPR Compliance</h2>
            <p>
                WiserBee, a company with its roots in Canada, already aligns closely with GDPR
                principles due to Canada's data protection framework. The EU has recognized Canada's
                privacy laws as providing adequate protection for data transfers. More information on this
                adequacy decision can be found here: <a href="https://ec.europa.eu/info/law/law-topic/data-protection/international-dimension-data-protection/adequacy-decisions_en">European Commission – Adequacy Decisions</a>.
            </p>
            <p>
                For EU operations, WiserBee utilizes AWS infrastructure located within the EU. Access
                to data by WiserBee's affiliate offices is limited to support and maintenance activities, and
                such access is governed by EU Standard Contractual Clauses to ensure compliance with
                GDPR.
            </p>

            <h2 className="my-4">Rights of Individuals under the GDPR</h2>
            <p>The GDPR grants individuals several rights regarding their personal data. Below is an
                overview of these rights and how they relate to WiserBee's platform.</p>

            <h3 className="my-3">1. Right to Be Informed</h3>
            <p>
                Individuals must be informed about data collection and processing. WiserBee processes
                personal data based on a contractual agreement with you and relies on your Organisation
                to obtain appropriate legal consent from end-users. If a user contacts WiserBee directly
                regarding data collection, we will direct them to your Organisation and notify you of the
                request.
            </p>

            <h3 className="my-3">2. Right to Access Personal Information</h3>
            <p>
                Individuals have the right to access their personal data. WiserBee users can view most
                of their personal data directly through the platform, depending on permissions (e.g.,
                administrators have more access than students). While WiserBee collects some system
                usage data (e.g., event logs, page views), it is typically anonymized. If you receive an
                access request requiring WiserBee's assistance, please contact WiserBee Support to
                open a service ticket. We aim to fulfil such requests within 30 days, and we will notify you
                if we believe it will take longer to complete.
            </p>
            <p>
                If WiserBee receives a data access request or subject access request directly from your
                users, we will instruct them to contact you, as you are the data controller.
            </p>

            <h3 className="my-3">3. Right to Erasure (Right to be Forgotten)</h3>
            <p>
                Individuals can request the deletion of their personal data when it is no longer necessary.
                However, this right is not absolute. If data is required to fulfil contractual obligations (e.g.,
                student records necessary for graduation), it may still be retained. For data to be erased,
                please contact WiserBee support to open a service ticket. Administrators at organisations
                can archive data but not perform a full erasure.
            </p>
            <p>
                WiserBee provides options to allow you to meet your data retention policies for your
                organisation.
            </p>

            <h3 className="my-3">4. Right to Restrict Processing</h3>
            <p>
                Individuals can request that data processing be restricted in specific cases, such as:
            </p>
            <ul>
                <li>When data is inaccurate and needs correction.</li>
                <li>When an objection to processing is under review.</li>
                <li>When processing is unlawful but the individual prefers restriction over deletion.</li>
                <li>When data is needed for legal claims.</li>
            </ul>
            <p>
                WiserBee provides tools to help you comply with these requirements.
                Correcting inaccurate data in most circumstances is under your control with the features
                available in WiserBee or by changing data with which WiserBee synchronises.
            </p>

            <h3 className="my-3">5. Right to Object to Automated Decision-Making & Profiling</h3>
            <p>
                If automated processing affects an individual's rights, they can:
            </p>
            <ul>
                <li>Request human intervention.</li>
                <li>Express their point of view.</li>
                <li>Obtain an explanation and challenge decisions.</li>
            </ul>
            <p>
                WiserBee's tools may generate course success predictions, but ultimate decisions require
                human action.
            </p>

            <h3 className="my-3">6. Right to Rectification</h3>
            <p>
                Individuals have the right to correct inaccurate personal data. Most data can be updated
                directly within WiserBee or in systems synchronized with it. If assistance is needed,
                WiserBee Support is available to help.
            </p>

            <h3 className="my-3">7. Right to Data Portability</h3>
            <p>
                This right allows individuals to obtain their data in a portable format. With WiserBee,
                courses, achievements, and grades information are not directly transferable to another
                school or corporation; i.e., the assignments, materials, achievements in an Introduction
                to Astronomy course may not be taught in a similar named course at another school.
            </p>
            <p>
                There should be no expectation of individuals to move certain elements of personal
                information (such as achievements, quiz attempts) within WiserBee to be imported into
                any other school or corporate Learning Management System. For educational customers,
                the student transcript is the key piece of information that is transferable between
                institutions to determine course equivalence. Students who are using ePortfolio can
                export their learning artifacts files and import them into another ePortfolio system.
            </p>

            <h3 className="my-3">8. Right to Object to Data Processing</h3>
            <p>
                Individuals may object to processing based on:
            </p>
            <ul>
                <li>Legitimate interests or public interest tasks.</li>
                <li>Direct marketing.</li>
                <li>Scientific or statistical research.</li>
            </ul>
            <p>
                WiserBee processes personal data as necessary to provide you with the services. If we
                receive an objection from your users, we will direct them to you.
            </p>
            <p>
                WiserBee does not use the data it collects through WiserBee tools and its associated
                products to market to our customers' end-users. Information about data collected for
                marketing purposes through WiserBee corporate websites and our privacy policy
                surrounding how we use that information may be found here:
                <a href="https://www.wiserbee.ca/legal/privacy/">https://www.wiserbee.ca/legal/privacy/</a>
            </p>

            <h2 className="my-4">Additional Resources</h2>
            <p>
                For more details on GDPR, visit the UK Information Commissioner's Office (ICO)
                website: <a href="https://ico.org.uk/for-organisations/guide-to-data-protection/guide-to-the-general-data-protection-regulation-gdpr/">ICO GDPR Overview</a>.
            </p>

            <h2 className="my-4">Legal Disclaimer</h2>
            <p className="font-bold">
                THIS DOCUMENT IS FOR INFORMATIONAL PURPOSES ONLY AND DOES NOT
                CONSTITUTE LEGAL ADVICE. FOR SPECIFIC GUIDANCE, CONSULT A
                PROFESSIONAL ADVISOR FAMILIAR WITH YOUR COMPLIANCE REQUIREMENTS.
            </p>
        </div>
    );
};

export default Gdpr;